import { Component, ErrorInfo, ReactNode } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { withTranslation } from "react-i18next";
import ErrorState from "./ErrorState";

interface Props {
  fallback?: ReactNode;
  children?: ReactNode;
  t: (_s: string) => string;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

export function isChunkLoadError(error: Error): boolean {
  const message = error?.message || "";
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Loading chunk") ||
    error.name === "ChunkLoadError"
  );
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isChunkError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "8rem",
              gap: "1rem",
            }}
          >
            <Typography variant="h4">
              {this.props.t("common.newVersionAvailable")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {this.props.t("common.newVersionDescription")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              sx={{ mt: 2 }}
              onClick={() => {
                window.location.reload();
              }}
            >
              {this.props.t("common.refresh")}
            </Button>
          </div>
        );
      }

      return (
        this.props.fallback ?? (
          <ErrorState onRetry={() => window.location.reload()} />
        )
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
